import { RouteName } from "@/router/extra";
import { useCommonContext } from "@/utils/context/common";
import useNavigation from "@/utils/useNavigate";
import { useAtmosphere, useBaseResource } from "@akc/biz-logic";
import {
  ResourceBannerComp,
  ResourceBannerV2Comp,
  ResourceCapsuleComp,
  ResourceCategoryDiamondComp,
  ResourceDiamondComp,
  ResourceFloorComp,
  ResourceHorizontalTabComp,
  ResourceImageV2Comp,
  ResourcePrimeBrandComp,
  ResourceProductShowcaseComp,
  ResourceSuperBrandComp,
  ResourceSuperExplosiveComp,
  ResourceTodaySellingComp,
  ResourceTodaySellingV2Comp,
} from "@akc/mx-taro-h5-components";
import React from "react";
import { useSearchParams } from "react-router-dom";
import CouponsBoard from "./new-person/CouponsBoardV2";
import styles from "./ResourceList.module.less";

type Props = {
  activityTagNo: string;
  activityTagIndex: number;
};

const ResourceList: React.FC<Props> = ({ activityTagNo, activityTagIndex }) => {
  const { skin, getSkinByKey } = useCommonContext();
  const { navigate2H5 } = useNavigation();
  // 页面query参数
  const [search] = useSearchParams();
  const previewRsc = search.get("previewRsc") || "0";
  const { data: resourceData } = useBaseResource({
    categoryId: activityTagNo,
    isThemeReady: true,
    preView: previewRsc === "1",
  });
  const { data: atmosphere } = useAtmosphere({ isBuyToday: true, type: 1 });

  // 跳转日历页面
  const toSaleCalendarPage = () => {
    navigate2H5(RouteName.SaleCalendar);
  };

  // 计算当前资源位的padding相关css类名
  const getSourcePaddingClassName = (resourceData, index) => {
    const crtItem = resourceData[index] || {};
    const preItem = resourceData[index - 1] || {};
    const nextItem = resourceData[index + 1] || {};

    // 当前组件是新商品橱窗
    if (crtItem.resourceType === "PRODUCT_WINDOW_V2") {
      const { componentStyle, containFlag } = crtItem?.productWindowVO || {};
      // 如果当前组件是一行三商品橱窗（componentStyle=2），且容器无边距（containFlag为false）且是第一个组件，则上方不留空隙
      const noTopPadding = componentStyle === 2 && !containFlag && index === 0;

      // 如果当前组件是一行三商品橱窗（componentStyle=2），且容器无边距（containFlag为false）且下一个组件为新版日期导航，则2个资源位直接不留空隙
      const noBtmPadding =
        componentStyle === 2 && !containFlag && nextItem?.resourceType === "NAVIGATE_V2";
      return `${noTopPadding ? styles.noTopPadding : ""} ${noBtmPadding ? styles.noBtmPadding : ""}`;
    }

    if (crtItem.resourceType === "IMG_V2") {
      const { componentStyle, marginType } = crtItem?.imgV2VO || {};
      // 如果当前组件是一行一图片（componentStyle=1），且无边距（marginType为1）且是第一个组件，则上方不留空隙
      const noTopPadding = componentStyle === 1 && marginType === 1 && index === 0;
      // 如果当前组件是一行一图片（componentStyle=1），且无边距（marginType为1）且下一个组件为新版日期导航，则2个资源位直接不留空隙
      const noBtmPadding =
        componentStyle === 1 && marginType === 1 && nextItem?.resourceType === "NAVIGATE_V2";
      return `${noTopPadding ? styles.noTopPadding : ""} ${noBtmPadding ? styles.noBtmPadding : ""}`;
    }

    // 当前组件是日期导航
    if (crtItem.resourceType === "NAVIGATE_V2") {
      // 上一个组件是无边距的一行一新版图片，或者无边距的一行三商品橱窗，则和当前组件之间不留空隙
      const preImg2 =
        preItem.resourceType === "IMG_V2" &&
        preItem?.imgV2VO?.componentStyle === 1 &&
        preItem?.imgV2VO?.marginType === 1;
      const preProductWindowV2 =
        preItem.resourceType === "PRODUCT_WINDOW_V2" &&
        preItem?.productWindowVO?.componentStyle === 2 &&
        !preItem?.productWindowVO?.containFlag;
      if (preImg2 || preProductWindowV2) {
        return styles.noTopPadding;
      }
      return "";
    }
    return "";
  };

  return (
    <>
      {resourceData?.map((resource, index) => {
        if (!resource) {
          return null;
        }
        // 新版今日必卖
        if (resource.resourceType === "TODAY_BUY_V2") {
          return (
            <div className={styles.taro_wrapper + " TODAY_BUY_V2"} key={index}>
              <ResourceTodaySellingV2Comp
                key={index}
                index={index}
                data={resource.todayBuyV2VO}
                locationCode="H5_ScheduleList_Marketing_Service"
                onMoreAction={toSaleCalendarPage}
                trackData={{
                  tab_rank: activityTagIndex,
                }}
              />
            </div>
          );
        }
        // 新版商品橱窗
        if (resource.resourceType === "PRODUCT_WINDOW_V2") {
          return (
            <div
              className={
                styles.taro_wrapper +
                " PRODUCT_WINDOW_V2 " +
                getSourcePaddingClassName(resourceData, index)
              }
              key={index}>
              <ResourceProductShowcaseComp
                key={index}
                index={index}
                data={resource.productWindowVO}
                trackData={{
                  tab_rank: activityTagIndex,
                }}
              />
            </div>
          );
        }
        // 新版图片组件
        if (resource.resourceType === "IMG_V2") {
          return (
            <div
              className={
                styles.taro_wrapper + " IMG_V2 " + getSourcePaddingClassName(resourceData, index)
              }
              key={index}>
              <ResourceImageV2Comp
                key={index}
                index={index}
                data={resource.imgV2VO}
                locationCode="H5_ScheduleList_Marketing_Service"
                trackData={{
                  tab_rank: activityTagIndex,
                }}
              />
            </div>
          );
        }
        // 新版横向导航
        if (resource.resourceType === "NAVIGATE_V2") {
          return (
            <div
              className={
                styles.taro_wrapper +
                " NAVIGATE_V2 " +
                getSourcePaddingClassName(resourceData, index)
              }
              key={index}>
              <ResourceHorizontalTabComp
                key={index}
                index={index}
                data={resource?.navigateV2VO?.navigateDateVOS || []}
                locationCode="H5_ScheduleList_Marketing_Service"
                onMoreAction={toSaleCalendarPage}
                trackData={{
                  tab_rank: activityTagIndex,
                  comp_name: resource?.navigateV2VO?.navigateType || "横向导航",
                }}
              />
            </div>
          );
        }
        if (resource.resourceType === "TODAYBUY") {
          // 今日必卖
          return (
            <ResourceTodaySellingComp
              style={{ width: "100%" }}
              key={index}
              index={index}
              model={resource.todayBuy}
              atmospheres={atmosphere}
              skinColorStr={getSkinByKey("todaySaleBgColor")}
              skinImageUrlStr={getSkinByKey("todaySaleBg")}
              onTopRightAction={() => toSaleCalendarPage()}
            />
          );
        }
        if (resource.resourceType === "DIAMOND") {
          // 金刚位
          return (
            <ResourceDiamondComp
              key={index}
              index={index}
              diamondList={resource.diamondList?.slice(0, 5)}
              skinColorStr={getSkinByKey("diamondBgColor")}
            />
          );
        }
        if (resource.resourceType === "CATEGORY_DIAMOND") {
          // 类目金刚位
          return (
            <ResourceCategoryDiamondComp
              key={index}
              index={index}
              categoryDiamondList={resource.categoryDiamondList}
            />
          );
        }
        if (resource.resourceType === "CATEGORY_BRANDIMG") {
          // 分类品牌图片（如大牌特卖2行10列）
          // 大牌特卖，全部tab（ activityTagNo === 10）读老皮肤，否则不需要皮肤
          return (
            <ResourcePrimeBrandComp
              key={index}
              index={index}
              model={resource.categoryBrandImgVO}
              skinColorStr={activityTagNo === "10" ? skin?.home?.primeBrandBgColor : ""}
              skinImageUrlStr={activityTagNo === "10" ? skin?.home?.primeBrandBg : ""}
            />
          );
        }
        if (resource.resourceType === "BANNER") {
          // 轮播图
          return (
            <div key={index}>
              <ResourceBannerComp
                key={index}
                index={index}
                bannerList={resource.bannerList}
                skinColorStr={getSkinByKey("resourceBannerBgColor")}
                skinImageUrlStr={getSkinByKey("resourceBannerBg")}
              />
            </div>
          );
        }
        if (resource.resourceType === "BANNER_V2") {
          // 轮播图
          return (
            <div key={index}>
              <ResourceBannerV2Comp
                key={index}
                index={index}
                data={resource.bannerV2VO}
                skinColorStr={getSkinByKey("resourceBannerBgColor")}
                skinImageUrlStr={getSkinByKey("resourceBannerBg")}
                trackData={{
                  tab_rank: activityTagIndex,
                }}
              />
            </div>
          );
        }
        if (resource.resourceType === "BRANDIMG") {
          // 品牌聚合图片（如超级品牌、王牌好货、（高佣、品牌新客、日日奖、预告...））
          return (
            <ResourceSuperBrandComp
              key={index}
              index={index}
              model={resource.brandImg}
              skinColorStr={getSkinByKey("brandsTogetherBgColor")}
              skinImageUrlStr={getSkinByKey("brandsTogetherBgImg")}
            />
          );
        }
        if (resource.resourceType === "PRODUCT_WINDOW") {
          // 商品橱窗（如超级爆品）
          return (
            <div key={index}>
              <ResourceSuperExplosiveComp
                key={index}
                index={index}
                model={resource.productWindow}
                skinColorStr={getSkinByKey("brandsTogetherBgColor")}
              />
            </div>
          );
        }
        if (resource.resourceType === "IMG") {
          // 普通图片（如每天10点抽888大红包）
          return (
            <ResourceFloorComp
              key={index}
              index={index}
              model={resource.imgComponent}
              skinColorStr={getSkinByKey("imageBgColor")}
            />
          );
        }
        if (resource.resourceType === "CAPSULE") {
          // 胶囊位（如热卖榜单、秒杀专区）
          return (
            <ResourceCapsuleComp
              key={index}
              index={index}
              model={resource.capsule}
              skinColorStr={getSkinByKey("brandsTogetherBgColor")}
            />
          );
        }
        if (resource.resourceType === "ZONECOUPON") {
          // 新人专区（专区优惠券）
          return <CouponsBoard key={index} />;
        }
        return null;
      })}
    </>
  );
};

export default ResourceList;
